import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Checkbox, FormControlLabel, IconButton, Menu, MenuItem, MenuList, Stack, styled, Tooltip, Typography } from '@mui/material'
import useSessionApi from 'components/cell-visualizations/useSessionApi'
import useSessionDownload from 'components/cell-visualizations/useSessionDownload'
import DeepcellPrimaryButton from 'components/shared/DeepcellPrimaryButton'
import { useState } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { DeepcellDialog, DeepcellTextField } from 'components/shared'
import { SaveSessionDialog, SaveSessionDialogProps } from './SaveSessionDialog'

const width = 180

const StyledMenuList = styled(MenuList)(({ theme }) => ({
  color: theme.palette.primary.main,
  width,
  paddingTop: 0,
  paddingBottom: 0,
}))

const StyledMenuItem = styled(MenuItem)({
  justifyContent: 'center',
})

export const SaveSessionMenu = (): JSX.Element => {
  const { setName, setProjectCode, cellVisualizations, dataFrame } = useCellVisualizationsSlice()
  const [openDialogue, setOpenDialogue] = useState(false)
  const [fileName, setFileName] = useState('')
  const [includeCellsData, setIncludeCellsData] = useState(false)

  const { sessionData, projectCode: projectCodeFromState } = cellVisualizations
  const nameFromState = sessionData?.name
  const { saveVersionConfig } = useSessionApi()
  const { downloadSession } = useSessionDownload()

  const [sessionButtonAnchor, setSessionButtonAnchor] = useState<Element>()
  const sessionButtonOpen = Boolean(sessionButtonAnchor)

  const [saveSessionDialogProps, setSaveSessionDialogProps] = useState<SaveSessionDialogProps>()

  const saveSession = async (name?: string, projectCode?: string, isCopy?: boolean) => {
    // Make sure to wait for save version to finish successfully before proceeding
    // it may encounter an error
    await saveVersionConfig({ name, projectCode, isCopy })

    if (name) setName(name)
    if (projectCode) setProjectCode(projectCode)
  }

  const handleSelectSave = (doSaveAs = false) => {
    setSessionButtonAnchor(undefined)

    if (doSaveAs || nameFromState === 'Untitled Session' || !nameFromState) {
      setSaveSessionDialogProps({
        title: `Save ${doSaveAs ? 'As New ' : ''}Session`,
        defaultName: doSaveAs ? nameFromState : undefined,
        defaultProjectCode: doSaveAs ? projectCodeFromState : undefined,
        isCopy: doSaveAs,
      })
    } else {
      saveSession(nameFromState, projectCodeFromState)
    }
  }

  const handleDownload = async () => {
    await downloadSession(fileName, includeCellsData)
    setSessionButtonAnchor(undefined)
    setOpenDialogue(false)
    // Reset checkbox state after download
    setIncludeCellsData(false)
  }

  const handleDialogClose = () => {
    setOpenDialogue(false)
    setIncludeCellsData(false)
  }

  return (
    <Box>
      <DeepcellPrimaryButton
        contained
        onClick={(e) => setSessionButtonAnchor(e.currentTarget)}
        sx={{ mr: 2, width }}
        color="info"
        endIcon={sessionButtonOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
      >
        Save Session
      </DeepcellPrimaryButton>
      <Menu
        anchorEl={sessionButtonAnchor}
        open={sessionButtonOpen}
        onClose={() => setSessionButtonAnchor(undefined)}
      >
        <StyledMenuList>
          <StyledMenuItem key={1} onClick={() => handleSelectSave()}>
            Save
          </StyledMenuItem>
          <StyledMenuItem key={2} onClick={() => handleSelectSave(true)}>
            Save As
          </StyledMenuItem>
          <StyledMenuItem
            key={3}
            disabled={!dataFrame}
            onClick={() => setOpenDialogue(true)}
          >
            Export
          </StyledMenuItem>
        </StyledMenuList>
      </Menu>
      <DeepcellDialog
        open={openDialogue}
        okLabel="Export"
        titleLabel="Export Session"
        handleCancel={handleDialogClose}
        handleConfirm={handleDownload}
      >
        <Stack spacing={2} sx={{ width: 500, px: 1 }}>
          <DeepcellTextField
            label="File name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="session-export.json"
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCellsData}
                  onChange={(e) => setIncludeCellsData(e.target.checked)}
                />
              }
              label="Include cell data with DL embeddings"
            />
            <Tooltip
              title={
                <>
                  <strong>What is this?</strong> Cell data includes all cell information (morphometric features, run metadata, 
                  and 64 deep learning embedding dimensions) for each cell in the session.
                  <br />
                  <br />
                  <strong>When to use:</strong> Check this if you need to analyze or process the raw cell data outside of this application, 
                  or if you need the DL embeddings for machine learning workflows. Leave unchecked for standard session backups.
                </>
              }
              arrow
              placement="top"
            >
              <IconButton size="small" sx={{ ml: 0.5, p: 0, height: '20px', width: '20px' }}>
                <InfoIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>
          {includeCellsData && (
            <Typography variant="caption" color="warning.main" sx={{ ml: 4, mt: -1, display: 'block' }}>
              ⚠️ Warning: Including cell data significantly increases file size (potentially 1-5 GB+ for large sessions)
              and may cause temporary memory usage spikes during export.
            </Typography>
          )}
        </Stack>
      </DeepcellDialog>
      <SaveSessionDialog
        {...saveSessionDialogProps}
        open={Boolean(saveSessionDialogProps)}
        onClose={() => setSaveSessionDialogProps(undefined)}
        onSaveSession={(d) => saveSession(d.name, d.projectCode, d.isCopy)}
      />
    </Box>
  )
}

export default SaveSessionMenu
